import {
  Badge,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';

import { consentStatusErrorCode, getConsentStatus } from '../../api/tenants';
import { getTenant, isTenantNotFound } from '../../api/tenantRegistry';
import ConsentMessageBar from '../identity/ConsentMessageBar';

const useStyles = makeStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '10px',
  },
});

// Non-blocking create-flow readiness for one tenant slot (FD-159, ADR-023 §4).
// Reuses the FD-111 consent probe (getConsentStatus) and, when the tenant is
// already in the registry (getTenant, 404-tolerant), surfaces service-principal
// presence and — for destination tenants — BPRT minting-account readiness. This
// is GUIDANCE ONLY: creation/save is never gated on any of it.
export interface TenantReadinessPanelProps {
  role: 'source' | 'destination';
  // The settled tenant GUID for the slot, or null when nothing to check yet.
  tenantId: string | null;
}

export default function TenantReadinessPanel({ role, tenantId }: TenantReadinessPanelProps) {
  const styles = useStyles();
  const roleLabel = role === 'source' ? 'Source' : 'Destination';

  // Consent probe (FD-111). staleTime keeps it quiet across re-renders.
  const consentQuery = useQuery({
    queryKey: ['tenant-consent-status', tenantId],
    queryFn: () => getConsentStatus(tenantId as string),
    enabled: Boolean(tenantId),
    retry: false,
    staleTime: 60_000,
  });

  // Registry lookup (FD-156). A 404 means "new tenant, not yet registered" —
  // expected during creation, never an error.
  const registryQuery = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenant(tenantId as string),
    enabled: Boolean(tenantId),
    retry: false,
    staleTime: 60_000,
  });

  if (!tenantId) return null;

  const consent = consentQuery.data;
  const showConsentNeeded = Boolean(consent && !consent.consented && consent.consentUrl);
  const showConsentUnverified =
    consentQuery.isError && consentStatusErrorCode(consentQuery.error) === 'GRAPH_ERROR';

  const registry = registryQuery.data;
  const isNewTenant = registryQuery.isError && isTenantNotFound(registryQuery.error);

  return (
    <div className={styles.panel}>
      <span className="font-display text-[11px] font-medium uppercase tracking-[0.06em] text-zinc-400">
        {roleLabel} tenant readiness
      </span>

      {/* Consent */}
      {consentQuery.isLoading ? (
        <span className="inline-flex items-center gap-2 text-zinc-500 text-sm">
          <Spinner size="tiny" /> Checking consent…
        </span>
      ) : showConsentNeeded && consent ? (
        <ConsentMessageBar
          consent={{
            tenantId: consent.tenantId,
            failureClass: consent.failureClass,
            missingRoles: consent.missingRoles,
            consentUrl: consent.consentUrl as string,
          }}
        />
      ) : showConsentUnverified ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Couldn&apos;t verify {role}-tenant consent</MessageBarTitle>
            This check is informational — you can still create the migration.
          </MessageBarBody>
          <MessageBarActions>
            <Button
              size="small"
              appearance="subtle"
              icon={<RefreshCw size={14} strokeWidth={1.5} />}
              onClick={() => consentQuery.refetch()}
            >
              Retry
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : consent?.consented ? (
        <span className="inline-flex items-center gap-2 text-sm text-zinc-600">
          <Badge appearance="tint" color="success">
            Consented
          </Badge>
          Graph permissions granted.
        </span>
      ) : null}

      {/* Registry readiness — SP presence + (destination) SA readiness */}
      {registryQuery.isLoading ? null : isNewTenant ? (
        <span className="text-sm text-zinc-500">
          New tenant — not yet in the registry. It is registered when the migration is created.
        </span>
      ) : registry ? (
        <div className="flex flex-col gap-1.5 text-sm">
          <ReadinessLine
            ok={registry.servicePrincipals.length > 0}
            okLabel="Tenant app present"
            notLabel="Tenant app not found in this tenant"
          />
          {role === 'destination' && (
            <ReadinessLine
              ok={Boolean(registry.bprtServiceAccount?.present)}
              okLabel="Minting service account configured"
              notLabel="Minting service account not configured (runbook step before wave activation)"
              neutralWhenNot
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

// A single readiness line with a status dot. `neutralWhenNot` renders the
// not-ready state as zinc (an expected-later condition, not an error) rather
// than red.
function ReadinessLine({
  ok,
  okLabel,
  notLabel,
  neutralWhenNot = false,
}: {
  ok: boolean;
  okLabel: string;
  notLabel: string;
  neutralWhenNot?: boolean;
}) {
  const dot = ok ? 'bg-green-600' : neutralWhenNot ? 'bg-zinc-400' : 'bg-amber-500';
  const text = ok ? 'text-zinc-600' : neutralWhenNot ? 'text-zinc-500' : 'text-amber-800';
  return (
    <span className={`inline-flex items-center gap-2 ${text}`}>
      <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" />
      {ok ? okLabel : notLabel}
    </span>
  );
}
